import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, RefreshCw } from 'lucide-react';
import {
  getInvoiceCustomFieldSettings,
  saveInvoiceCustomFieldSettings,
  type BmpCustomFieldSource,
} from '../../features/integrations/blume/invoiceCustomFieldsApi';
import './BlumeInvoiceCustomFieldsPanel.scss';

const sources: Array<{ key: BmpCustomFieldSource; label: string; detail: string }> = [
  { key: 'PO_NUMBER', label: 'BMP PO number', detail: 'Order.poNumber' },
  { key: 'DEFEO_TICKET_NUMBER', label: 'BMP DeFeo Ticket #', detail: 'OrderItem.ticketNumber' },
];

export function BlumeInvoiceCustomFieldsPanel() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['blume-invoice-custom-fields-settings'],
    queryFn: getInvoiceCustomFieldSettings,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
  const [selection, setSelection] = useState<Record<BmpCustomFieldSource, string>>({
    PO_NUMBER: '',
    DEFEO_TICKET_NUMBER: '',
  });
  const [keyLoaded, setKeyLoaded] = useState('');
  const [touched, setTouched] = useState<Record<BmpCustomFieldSource, boolean>>({
    PO_NUMBER: false,
    DEFEO_TICKET_NUMBER: false,
  });
  const dataKey = useMemo(
    () =>
      query.data
        ? JSON.stringify({
            realm: query.data.catalog.realmId,
            po: query.data.mappings.PO_NUMBER?.updatedAt,
            ticket: query.data.mappings.DEFEO_TICKET_NUMBER?.updatedAt,
          })
        : '',
    [query.data]
  );
  useEffect(() => {
    if (!query.data || !dataKey || dataKey === keyLoaded) return;
    setSelection({
      PO_NUMBER: query.data.mappings.PO_NUMBER?.definitionId ?? '',
      DEFEO_TICKET_NUMBER: query.data.mappings.DEFEO_TICKET_NUMBER?.definitionId ?? '',
    });
    setTouched({ PO_NUMBER: false, DEFEO_TICKET_NUMBER: false });
    setKeyLoaded(dataKey);
  }, [dataKey, keyLoaded, query.data]);

  const save = useMutation({
    mutationFn: () =>
      saveInvoiceCustomFieldSettings({
        PO_NUMBER: selection.PO_NUMBER || null,
        DEFEO_TICKET_NUMBER: selection.DEFEO_TICKET_NUMBER || null,
      }),
    onSuccess: async (result) => {
      qc.setQueryData(['blume-invoice-custom-fields-settings'], result);
      setKeyLoaded('');
      await qc.invalidateQueries({ queryKey: ['blume-invoice-custom-fields-settings'] });
    },
  });
  const fields = query.data?.catalog.fields ?? [];
  const known = new Set(fields.map((f) => f.definitionId));
  const duplicated = !!selection.PO_NUMBER && selection.PO_NUMBER === selection.DEFEO_TICKET_NUMBER;
  const invalid =
    duplicated ||
    sources.some((s) => {
      const chosen = selection[s.key];
      const saved = query.data?.mappings[s.key];
      return (
        (!!chosen && !known.has(chosen)) ||
        Boolean(saved && !saved.valid && chosen === saved.definitionId && !touched[s.key])
      );
    });

  return (
    <section className="blume-qbo-custom-fields">
      <div className="blume-qbo-custom-fields__header">
        <div>
          <h3>Invoice custom field mappings</h3>
          <p>
            Choose which fields in the connected QuickBooks company receive BMP’s PO and DeFeo
            ticket numbers. Values are taken from the saved BMP order, not from the browser.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          disabled={query.isFetching || save.isPending}
          onClick={() => query.refetch()}
        >
          <RefreshCw size={14} /> Refresh QBO fields
        </button>
      </div>
      {query.isLoading && <p>Loading current QuickBooks invoice fields…</p>}
      {query.error && (
        <p className="blume-qbo-custom-fields__error">{(query.error as Error).message}</p>
      )}
      {query.data && (
        <>
          <small>
            QuickBooks company: {query.data.catalog.realmId} · Legacy REST fields only. Enhanced
            GraphQL-only fields are not shown. A sales-form-enabled field may still require
            invoice-specific verification in QuickBooks.
          </small>
          <div className="blume-qbo-custom-fields__grid">
            {sources.map((s) => {
              const saved = query.data.mappings[s.key];
              const chosen = selection[s.key];
              const other = sources.find((x) => x.key !== s.key)!.key;
              return (
                <div className="blume-qbo-custom-fields__card" key={s.key}>
                  <label htmlFor={`invoice-custom-field-${s.key}`}>{s.label}</label>
                  <small>{s.detail}</small>
                  <select
                    id={`invoice-custom-field-${s.key}`}
                    value={chosen}
                    onChange={(event) => {
                      setSelection((current) => ({ ...current, [s.key]: event.target.value }));
                      setTouched((current) => ({ ...current, [s.key]: true }));
                    }}
                  >
                    <option value="">Not mapped</option>
                    {saved && !known.has(saved.definitionId) && (
                      <option value={saved.definitionId} disabled>
                        Previously mapped: {saved.savedName} (not available in this company)
                      </option>
                    )}
                    {fields
                      .filter(
                        (f) => f.definitionId !== selection[other] || f.definitionId === chosen
                      )
                      .map((f) => (
                        <option key={f.definitionId} value={f.definitionId}>
                          {f.name} (QBO ID {f.definitionId})
                        </option>
                      ))}
                  </select>
                  {saved && !saved.valid && (
                    <p className="blume-qbo-custom-fields__error">
                      Saved mapping is stale or belongs to another QBO company. Re-select an enabled
                      field and save.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {fields.length === 0 && (
            <p>
              No enabled legacy REST invoice fields were found. Create/enable transaction text
              fields in QuickBooks. Enhanced-only fields are not yet supported by this integration.
            </p>
          )}
          {duplicated && (
            <p className="blume-qbo-custom-fields__error">
              PO number and DeFeo ticket cannot map to the same QuickBooks field.
            </p>
          )}
          {save.error && (
            <p className="blume-qbo-custom-fields__error">{(save.error as Error).message}</p>
          )}
          {save.isSuccess && <p role="status">Invoice custom field mappings saved.</p>}
          <div className="blume-qbo-custom-fields__actions">
            <button
              className="btn btn--primary"
              type="button"
              disabled={save.isPending || invalid || query.isFetching}
              onClick={() => save.mutate()}
            >
              <Save size={16} />
              {save.isPending ? 'Saving…' : 'Save custom field mappings'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
